import { default as AntCalendar } from "antd/lib/calendar";
import { pipe, Option, ReadonlyArray } from "fp";

import * as Moment from "moment";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Distance } from "~/Distance";
import * as Driver from "~/Driver";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import * as Log from "~/Log";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

export function Calendar(props: Calendar.Props) {
  const data = Calendar.Data.fromSummaries(props);
  const [targetDate, setTargetDate] = useState<Time.DateTime>(
    Time.DateTime.now()
  );

  useEffect(() => {
    setTargetDate(Time.DateTime.now());
  }, [props.interval]);

  const [mode, modeUpdate] = React.useState<"month" | "year">("month");
  return (
    <Calendar.Container left bottom right>
      <AntCalendar
        mode={mode}
        value={Moment.default(Time.DateTime.toValue(targetDate))}
        onSelect={(moment) =>
          setTargetDate(Time.DateTime.fromNumber(moment.valueOf()))
        }
        onPanelChange={(_moment, updatedMode) => {
          if (mode === updatedMode) return;
          if (mode === "year") return modeUpdate("month");
          if (mode === "month") return modeUpdate("year");
        }}
        monthCellRender={(moment) => (
          <Calendar.Month
            {...props}
            {...data}
            mode={mode}
            setMode={modeUpdate}
            switchToMonth={setTargetDate}
            dateTime={moment.valueOf()}
          />
        )}
        dateCellRender={(moment) => (
          <Calendar.Day
            {...props}
            {...data}
            mode={mode}
            dateTime={moment.valueOf()}
          />
        )}
      />
      {props.isLoading && (
        <Calendar.Loading>
          <Typography.H2.Component>
            <Loading.Component />
          </Typography.H2.Component>
        </Calendar.Loading>
      )}
    </Calendar.Container>
  );
}

export namespace Calendar {
  export type Props = Log.Summaries.PropsWithData &
    Log.Summaries.Toggle.Props & { readonly isLoading?: boolean };

  export type Mode = "year" | "month";

  export const useJumpToTable = ({
    toggle,
    period,
  }: Log.Summaries.Toggle.Props & { readonly period: "month" | "day" }) => {
    const useURLUpdate = Log.Summaries.Table.Settings.useURLUpdate();
    return (
      dateTimeOrMoment: Time.DateTime | Moment.Moment,
      issue?: Log.Summary.Issue
    ) => {
      const moment =
        "dateTime" in dateTimeOrMoment
          ? Moment.default(dateTimeOrMoment.dateTime)
          : dateTimeOrMoment;

      const start = Moment.default(moment).startOf(period).valueOf();
      const stop =
        period === "day"
          ? start
          : Moment.default(Moment.default(moment).endOf(period).valueOf())
              .startOf("day")
              .valueOf();

      if (period === "day" || issue) {
        // eslint-disable-next-line functional/no-expression-statement
        toggle.update("table");
      }

      // eslint-disable-next-line functional/no-expression-statement
      useURLUpdate({
        ...(issue ? { issues: [issue] } : {}),
        ...Time.Interval.Stopped.fromDateTimes(
          Time.DateTime.fromNumber(start),
          Time.DateTime.fromNumber(stop)
        ),
      });
    };
  };

  export const useJumpToMonthCalendar = ({
    setMode,
    switchToMonth,
  }: {
    readonly setMode: (mode: Mode) => void;
    readonly switchToMonth: ({ dateTime }: Time.DateTime) => void;
  }) => {
    const useURLUpdate = Log.Summaries.Table.Settings.useURLUpdate();
    return (dateTimeOrMoment: Time.DateTime | Moment.Moment) => {
      const moment =
        "dateTime" in dateTimeOrMoment
          ? Moment.default(dateTimeOrMoment.dateTime)
          : dateTimeOrMoment;

      const start = Moment.default(moment).startOf("month").valueOf();
      const stop = Moment.default(moment).endOf("month").valueOf();

      // eslint-disable-next-line functional/no-expression-statement
      setMode("month");

      // eslint-disable-next-line functional/no-expression-statement
      switchToMonth(Time.DateTime.fromNumber(start));

      // eslint-disable-next-line functional/no-expression-statement
      useURLUpdate({
        ...Time.Interval.Stopped.fromDateTimes(
          Time.DateTime.fromNumber(start),
          Time.DateTime.fromNumber(stop)
        ),
      });
    };
  };

  export const Container = styled(Layout.Block.Component)`
    position: relative;

    .ant-picker-calendar-date-value {
      position: absolute;
      top: 4px;
      right: 4px;
      font-weight: 900;
    }

    .ant-picker-calendar-full
      .ant-picker-panel
      .ant-picker-calendar-date-content {
      height: 130px;
    }
  `;

  export const Loading = styled.div`
    z-index: 10;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    opacity: 0.8;
    background: white;

    text-align: center;
  `;

  export type Data = {
    readonly data: { readonly [date: string]: Datum } & {
      readonly [month: string]: Datum;
    };
  };

  export namespace Data {
    const empty = (): Data => ({ data: {} });

    export const fromSummaries = ({ summaries }: Log.Summaries) =>
      pipe(
        summaries,
        ReadonlyArray.reduce<Log.Summary, Data>(
          empty(),
          (previous, { summary }) => {
            const date = Time.DateTime.toDateOnly(summary);
            const month = Time.DateTime.toMonth(summary);
            const year = Time.DateTime.toYear(summary);
            return {
              data: {
                ...previous.data,
                [date]: Datum.concat(
                  getDay({ ...previous, ...summary }),
                  Datum.fromSummary({ summary })
                ),
                [`${month}${year}`]: Datum.concat(
                  getMonth({ ...previous, ...summary }),
                  Datum.fromSummary({ summary })
                ),
              },
            };
          }
        )
      );

    export const getDay = (selection: Time.DateTime & Data): Datum =>
      selection.data[Time.DateTime.toDateOnly(selection)] ?? Datum.empty();

    export const getMonth = (selection: Time.DateTime & Data): Datum => {
      const month = Time.DateTime.toMonth(selection);
      const year = Time.DateTime.toYear(selection);
      return selection.data[`${month}${year}`] ?? Datum.empty();
    };
  }

  type Datum = Log.Summaries & Total;
  namespace Datum {
    export const isEmpty = (datum: Datum): boolean => Total.isEmpty(datum);

    export const empty = (): Datum => ({
      ...Log.Summaries.empty(),
      ...Total.empty(),
    });

    export const concat = (a: Datum, b: Datum): Datum => ({
      ...Log.Summaries.concat(a, b),
      ...Total.concat(a, b),
    });

    export const fromSummary = (summary: Log.Summary): Datum => ({
      summaries: [summary],
      ...Total.fromSummary(summary),
    });
  }

  export type Total = Log.Summary.Issues.Total & {
    readonly total: Distance & Time.Duration;
  };

  export namespace Total {
    export const isEmpty = ({
      total: { distance, duration, issues },
    }: Total): boolean =>
      distance.miles + duration <= 0 &&
      Log.Summary.Issues.Total.isEmpty({ total: { issues } });

    export const empty = (): Total => ({
      total: {
        distance: { miles: 0 },
        duration: 0,
        ...Log.Summary.Issues.Total.empty().total,
      },
    });

    export const concat = (a: Total, b: Total): Total => ({
      total: {
        distance: { miles: a.total.distance.miles + b.total.distance.miles },
        duration: a.total.duration + b.total.duration,
        ...Log.Summary.Issues.Total.concat(a, b).total,
      },
    });

    export const fromSummary = ({
      summary: { distance, duration, ...summary },
    }: Log.Summary): Total => ({
      total: {
        distance: distance,
        duration: duration,
        ...Log.Summary.Issues.Total.fromIssues(summary).total,
      },
    });
  }

  export function Day({ driver, ...props }: Day.Props) {
    const jumpToTable = useJumpToTable({ ...props, period: "day" });

    const rowRoute = Log.Summaries.Table.Settings.useRowRoute();
    useEffect(() => rowRoute(), []);

    return pipe(
      Option.fromNullable(Data.getDay(props)),
      Option.chain(
        Option.fromPredicate(
          (datum) => !!datum.summaries[0] && !Datum.isEmpty(datum)
        )
      ),
      Option.map(({ total, summaries }) => {
        const summary = driver ? summaries[0].summary : undefined;

        const Issues = () =>
          summary ? (
            <Log.Summary.Issues NotApplicable={() => null} {...summary} />
          ) : (
            <Log.Summary.Issues.Total
              total={total}
              onClick={(issue) => jumpToTable(props, issue)}
            />
          );

        const Details = () =>
          !summary ? null : (
            <>
              <Distance NotApplicable={() => null} {...(summary ?? total)} />
              <Day.Details>
                <Log.Summary.Duration
                  NotApplicable={() => null}
                  summary={summary ?? total}
                />
                <Log.Summary.Exemption
                  NotApplicable={() => null}
                  summary={summary}
                />
              </Day.Details>
            </>
          );

        return (
          <Day.Container
            onClick={(event) => {
              // eslint-disable-next-line functional/no-expression-statement
              event.stopPropagation();
              return summary ? rowRoute(summary) : jumpToTable(props);
            }}
          >
            <Day.Content>
              <Day.Issues>
                <Issues />
              </Day.Issues>
              <Details />
            </Day.Content>
          </Day.Container>
        );
      }),
      Option.toNullable
    );
  }

  export namespace Day {
    export type Props = Time.DateTime &
      Log.Summaries.Toggle.Props &
      Data & { readonly driver?: Driver.Driver } & {
        readonly mode: Calendar.Mode;
      };

    export const Container = styled.div`
      height: 100%;
      width: 100%;
    `;

    export const Content = styled.div`
      margin-right: 24px;
      font-size: ${Typography.FontSize.data()};
    `;

    export const Details = styled.div`
      display: flex;
      flex-direction: column;
      padding-bottom: 4px;
      & > * {
        display: inline-flex;
      }
    `;

    export const Issues = styled.div`
      padding-bottom: 8px;
    `;
  }

  export function Month({ driver, ...props }: Month.Props) {
    const jumpToTable = useJumpToTable({ ...props, period: "month" });
    const jumpToCalendar = useJumpToMonthCalendar(props);
    return pipe(
      Option.fromNullable(Data.getMonth(props)),
      Option.chain(Option.fromPredicate((datum) => !Datum.isEmpty(datum))),
      Option.map(({ total }) => {
        const Details = () =>
          !driver ? null : (
            <Month.Description top>
              {total.distance.miles > 0 && (
                <>
                  <Distance NotApplicable={() => null} {...total} />
                  &nbsp;over&nbsp;
                </>
              )}
              <Log.Summary.Duration summary={total} />
              &nbsp;of drive time.
            </Month.Description>
          );

        return (
          <Month.Container
            onClick={(event) => {
              // eslint-disable-next-line functional/no-expression-statement
              event.stopPropagation();
              return jumpToCalendar(props);
            }}
          >
            <Month.Content>
              <Log.Summary.Issues.Total
                total={total}
                onClick={(issue) => jumpToTable(props, issue)}
                {...props}
              />
              <Details />
            </Month.Content>
          </Month.Container>
        );
      }),
      Option.toNullable
    );
  }

  export namespace Month {
    export type Props = Day.Props & {
      readonly setMode: (mode: "month" | "year") => void;
      readonly switchToMonth: (targetMonth: Time.DateTime) => void;
    };

    export const Container = styled.div`
      height: 100%;
      width: 100%;
    `;

    export const Content = styled.div`
      margin-right: 28px;
    `;

    export const Description = styled.div`
      ${Layout.Spacing.styles(Layout.Spacing.margin())({
        all: Layout.Inline.padding(),
      })}
    `;
  }
}
